import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'About', path: '/about' },
  { label: 'Location', path: '/location' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [announcementVisible, setAnnouncementVisible] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* ── TOP ANNOUNCEMENT BAR ─────────────────────────────── */}
      {announcementVisible && !scrolled && (
        <div
          className="fixed top-0 left-0 right-0 z-[60] bg-teal border-b border-teal-mid"
        >
          <div className="max-w-[1400px] mx-auto px-8 h-9 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-white/60 flex-shrink-0" />
              <span className="text-white/70 text-[11px] font-sans tracking-wide hidden sm:block">
                Available Now — 24/7 Emergency Drain &amp; Sewer Service Throughout San Bernardino &amp; Inland Empire
              </span>
              <span className="text-white/70 text-[11px] font-sans tracking-wide sm:hidden">
                24/7 Emergency Service Available
              </span>
            </div>
            <div className="flex items-center gap-5">
              <a href="tel:+19090000000" className="flex items-center gap-1.5 text-white/80 hover:text-white text-[11px] font-sans cursor-pointer transition-colors whitespace-nowrap">
                <i className="ri-phone-line text-[10px]" />
                Call Now
              </a>
              <button
                onClick={() => setAnnouncementVisible(false)}
                className="w-5 h-5 flex items-center justify-center text-white/40 hover:text-white/75 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xs" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MAIN HEADER ──────────────────────────────────────── */}
      <header
        className={`fixed left-0 right-0 z-50 transition-all duration-500 ${announcementVisible && !scrolled ? 'top-9' : 'top-0'} ${
          scrolled
            ? 'bg-white/97 border-b border-cream-dark'
            : 'bg-transparent'
        }`}
        style={{
          backdropFilter: scrolled ? 'blur(24px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(24px)' : 'none',
          boxShadow: scrolled ? '0 2px 36px rgba(0,0,0,0.07)' : 'none',
        }}
      >
        {/* Gold top accent on scroll */}
        <div className={`absolute top-0 left-0 right-0 h-[2px] bg-gold transition-opacity duration-500 ${scrolled ? 'opacity-100' : 'opacity-0'}`} />

        <div className="max-w-[1400px] mx-auto px-8 flex items-center justify-between h-[76px]">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer group flex-shrink-0">
            <div className="flex items-center justify-center flex-shrink-0 w-10 h-10">
              <img
                src="https://d3p2r6ofnvoe67.cloudfront.net/fit-in/198x198/filters:strip_exif()/filters:no_upscale()/filters:format(webp)/media/fbbe76ba-f692-40ed-9f89-bcbaa7785d8a.png"
                alt="Drain & Sewer Pro Logo"
                className="w-10 h-10 object-contain transition-transform duration-300 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-col leading-tight">
              <span className={`font-serif font-bold text-[14px] tracking-[0.1em] transition-colors duration-500 whitespace-nowrap ${scrolled ? 'text-navy' : 'text-white'}`}>
                DRAIN &amp; SEWER PRO
              </span>
              <span className={`text-[9px] tracking-[0.24em] uppercase font-sans transition-colors duration-500 ${scrolled ? 'text-gold' : 'text-gold/72'}`}>
                San Bernardino, CA
              </span>
            </div>
          </Link>

          {/* Desktop centered nav */}
          <nav className="hidden lg:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-[12px] font-medium tracking-[0.1em] transition-all duration-300 cursor-pointer whitespace-nowrap uppercase relative group ${
                  location.pathname === link.path
                    ? 'text-gold'
                    : scrolled
                    ? 'text-navy/58 hover:text-navy'
                    : 'text-white/68 hover:text-white'
                }`}
              >
                {link.label}
                <span className={`absolute -bottom-0.5 left-0 right-0 h-[1.5px] bg-gold transition-transform duration-300 origin-left ${location.pathname === link.path ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} />
              </Link>
            ))}
          </nav>

          {/* Desktop CTA area */}
          <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
            <a
              href="tel:+19090000000"
              className={`flex items-center gap-2 cursor-pointer whitespace-nowrap transition-all duration-300 group ${scrolled ? 'text-navy/55 hover:text-navy' : 'text-white/55 hover:text-white'}`}
            >
              <div className={`w-8 h-8 flex items-center justify-center rounded-full border transition-all duration-300 ${scrolled ? 'border-navy/12 group-hover:border-gold/50 group-hover:text-gold' : 'border-white/18 group-hover:border-gold/50 group-hover:text-gold'}`}>
                <i className="ri-phone-line text-xs" />
              </div>
              <span className="text-[12px] font-semibold tracking-wide">24/7 Emergency</span>
            </a>
            <Link
              to="/location"
              className="bg-gold hover:bg-gold-mid text-white text-[11px] font-semibold px-7 py-[12px] rounded transition-all duration-300 cursor-pointer whitespace-nowrap tracking-[0.12em] uppercase"
            >
              Free Estimate
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl transition-colors duration-500 ${scrolled ? 'text-navy' : 'text-white'} ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} />
          </button>
        </div>

        {/* Mobile menu */}
        <div className={`lg:hidden bg-white border-t border-cream-dark transition-all duration-300 overflow-hidden ${menuOpen ? 'max-h-[460px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="px-8 py-6 flex flex-col gap-0.5">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-[12px] font-medium tracking-[0.1em] uppercase py-3.5 border-b border-cream-dark last:border-b-0 cursor-pointer transition-colors ${location.pathname === link.path ? 'text-gold' : 'text-navy/58 hover:text-gold'}`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-5 space-y-3">
              <a
                href="tel:+19090000000"
                className="flex items-center justify-center gap-2 border border-cream-dark text-navy text-[12px] font-semibold py-3.5 rounded cursor-pointer whitespace-nowrap hover:border-gold hover:text-gold transition-all"
              >
                <i className="ri-phone-line" />
                24/7 Emergency Line
              </a>
              <Link
                to="/location"
                className="block text-center bg-gold text-white text-[12px] font-semibold py-3.5 rounded cursor-pointer whitespace-nowrap tracking-[0.12em] uppercase hover:bg-gold-mid transition-colors"
              >
                Free Estimate
              </Link>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Navbar;
